/**
 * Harmony Streaming Parser
 *
 * Parses GPT-oss structured responses that use channel tags to separate
 * reasoning (analysis) from final response content.
 *
 * Format: <|channel|>analysis<|message|>...reasoning...<|end|><|channel|>final<|message|>...response...<|end|>
 *
 * Used by both the chat UI (MessageContainer) and the export service
 * to consistently extract reasoning blocks from assistant messages.
 */

export type SplitResult = { analysis: string; final: string };

export const START = '<|start|>';
export const END = '<|end|>';
export const CHAN = '<|channel|>';
export const MSG = '<|message|>';
const ASSISTANT_ROLE_TOKEN = 'assistant';

function isRolePreamble(s: string): boolean {
    const normalized = (s || '').replace(/<\|[^|]{1,64}\|>/g, ' ').replace(/<\/?think>/gi, ' ').trim().toLowerCase();
    return normalized === 'assistant' || normalized === 'assistant:';
}

function stripAssistantPrefix(s: string): string {
    return resolveAssistantPrefix(s).content;
}

export function resolveAssistantPrefix(
    content: string,
    options: { allowPending?: boolean } = {},
) {
    const allowPending = options.allowPending ?? false;
    if (!content) return { content, pending: false };

    const leadingWhitespace = content.match(/^\s*/)![0];
    const body = content.slice(leadingWhitespace.length);
    const lowerBody = body.toLowerCase();

    if (!body) {
        return allowPending
            ? { content: '', pending: true }
            : { content, pending: false };
    }
    if (
        body.length < ASSISTANT_ROLE_TOKEN.length &&
        ASSISTANT_ROLE_TOKEN.startsWith(lowerBody)
    ) {
        return allowPending
            ? { content: '', pending: true }
            : { content, pending: false };
    }
    if (!lowerBody.startsWith(ASSISTANT_ROLE_TOKEN)) {
        return { content, pending: false };
    }

    let rest = body.slice(ASSISTANT_ROLE_TOKEN.length);
    const hadColon = rest.startsWith(':');
    if (hadColon) rest = rest.slice(1);

    if (!rest.trim()) {
        return allowPending
            ? { content: '', pending: true }
            : { content: '', pending: false };
    }

    const trimmedRest = rest.replace(/^\s+/, '');
    if (!trimmedRest) {
        return allowPending
            ? { content: '', pending: true }
            : { content: '', pending: false };
    }

    if (!hadColon && /^\s+[a-z]/.test(rest)) {
        return { content, pending: false };
    }

    return { content: leadingWhitespace + trimmedRest, pending: false };
}

/**
 * Strip hidden thinking tags and harmony protocol tags from text.
 */
export function stripHiddenAndTags(s: string): string {
    return (s || '')
        .replace(/<think>[\s\S]{0,1000000}?<\/think>/gi, '')
        .replace(/<\/think>/gi, '')
        .replace(/<\|start\|>/g, '')
        .replace(/<\|end\|>/g, '')
        .replace(/<\|channel\|>[\s\S]{0,256}?<\|message\|>/g, '')
        .trim();
}

/**
 * Split a GPT-oss harmony streaming response into analysis and final parts.
 *
 * If the content contains no channel tags, the entire content is returned
 * as `final` with an empty `analysis`.
 */
export function splitHarmonyStreaming(raw: string): SplitResult {
    if (!raw) return { analysis: '', final: '' };

    if (!raw.includes(CHAN)) {
        return { analysis: '', final: stripAssistantPrefix(stripHiddenAndTags(raw)) };
    }

    let i = 0;
    const n = raw.length;
    let mode: 'outside' | 'analysis' | 'final' | 'other' = 'outside';
    let hasSeenChannel = false;

    const analysisParts: string[] = [];
    const finalParts: string[] = [];

    while (i < n) {
        const nextChan = raw.indexOf(CHAN, i);
        const nextEnd = raw.indexOf(END, i);

        if (mode === 'outside') {
            // Any plain text before the next channel is final text.
            const cut = nextChan === -1 ? n : nextChan;
            if (cut > i) {
                const prelude = raw.slice(i, cut);
                if (hasSeenChannel || !isRolePreamble(prelude)) {
                    finalParts.push(prelude);
                }
            }
            if (nextChan === -1) break;

            // Read channel name
            const afterChan = nextChan + CHAN.length;
            const msgIdx = raw.indexOf(MSG, afterChan);
            if (msgIdx === -1) break; // partial tag, stop cleanly
            const channelName = raw.slice(afterChan, msgIdx).trim().toLowerCase();
            i = msgIdx + MSG.length;
            hasSeenChannel = true;

            if (channelName === 'analysis') mode = 'analysis';
            else if (channelName === 'final') mode = 'final';
            else mode = 'other';
            continue;
        }

        // We are inside a channel; take until <|end|> or end-of-string
        if (nextEnd === -1) {
            const seg = raw.slice(i, n);
            if (mode === 'analysis') analysisParts.push(seg);
            else if (mode === 'final') finalParts.push(seg);
            // for "other" channels we ignore
            break;
        } else {
            const seg = raw.slice(i, nextEnd);
            if (mode === 'analysis') analysisParts.push(seg);
            else if (mode === 'final') finalParts.push(seg);
            i = nextEnd + END.length;
            mode = 'outside';
            continue;
        }
    }

    return {
        analysis: stripHiddenAndTags(analysisParts.join('')),
        final: stripAssistantPrefix(stripHiddenAndTags(finalParts.join(''))),
    };
}
